/**
 * Log levels
 */
export const LEVELS = {
  trace: 1,
  debug: 2,
  info: 3,
  log: 4,
  warn: 5,
  error: 6,
  silent: 100,
}

type LevelsKeys = keyof typeof LEVELS
type LevelsValues = (typeof LEVELS)[LevelsKeys]
type LevelsMap = Map<string, LevelsValues>

/**
 * Log level `localStorage` persist key
 */
const LOG_PERSIST_KEY = 'tria__log'

/**
 * Default log level
 */
const DEFAULT_LOG_LEVEL =
  process.env.NODE_ENV === 'production' ? 'error' : 'trace,player=silent'

/**
 * Log level map
 */
let log: LevelsMap = parse(get())

/**
 * Log level check function
 * Checks logger level and all its parents loggers levels
 * Returns `true` if logger level is less than or equal to the requested level,
 * meaning that logger will log messages of this level, and `false` otherwise.
 */
export const check = (logger: string, def?: LevelsValues) => {
  const DEF = LEVELS[DEFAULT_LOG_LEVEL as LevelsKeys]
  const loggers: string[] = []
  do {
    loggers.push(logger)
    const idx = logger.lastIndexOf('/')
    if (idx < 0) break
    logger = logger.slice(0, idx)
  } while (logger)

  return (lvl: LevelsValues): boolean => {
    let level: LevelsValues | undefined
    for (const logger of loggers) {
      level = log.get(logger)
      if (level) break
    }
    return (level || def || log.get('') || DEF) <= lvl
  }
}

/**
 * Get log level from `localStorage`
 */
function get(): string | null | undefined {
  try {
    return localStorage.getItem(LOG_PERSIST_KEY)
  } catch (ignore) {
    // ignore errors
  }
}

/**
 * Set log level to `localStorage`
 */
function set(level?: string | null): void {
  try {
    if (level == null) {
      localStorage.removeItem(LOG_PERSIST_KEY)
    } else {
      localStorage.setItem(LOG_PERSIST_KEY, level)
    }
  } catch (ignore) {
    // ignore errors
  }
}

/**
 * Parse log level string into a map of { logger => level }
 */
function parse(log?: string | null): LevelsMap {
  // default log level
  log = log || DEFAULT_LOG_LEVEL

  const map: LevelsMap = new Map()
  for (const logger of log.split(',')) {
    let [name, level] = logger.split('=', 2)
    if (!name) continue
    if (!level) {
      level = name
      name = ''
    }
    if (level in LEVELS) {
      map.set(name, LEVELS[level as LevelsKeys])
    }
  }

  // set default log level if no levels were parsed
  if (map.size === 0) {
    map.set('', LEVELS[DEFAULT_LOG_LEVEL as LevelsKeys])
  }

  return map
}

/**
 * Stringify log level map into a string
 */
function stringify(map: LevelsMap): string {
  const strs: string[] = []
  for (const [name, level] of map.entries()) {
    const lvl = Object.keys(LEVELS).find(
      (key) => LEVELS[key as LevelsKeys] === level
    )
    if (!lvl) continue // skip unknown levels (should not happen)
    strs.push(name ? name + '=' + lvl : lvl)
  }
  return strs.join(',')
}

// expose global `LOG` variable, so it allows to change log level from devtools console
Object.defineProperty(globalThis, 'LOG', {
  get: () => stringify(log),
  set: (value?: string | null) => {
    log = parse(value)
    const str = stringify(log)
    set(str === DEFAULT_LOG_LEVEL ? undefined : str)
  },
})
